import { Link } from '@inertiajs/react'
import PlatformLayout from '@/components/PlatformLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus } from 'lucide-react'

interface SchoolRow {
  id: number
  name: string
  phone: string | null
  city: string | null
  state: string | null
  slug: string | null
  active: boolean
  owner_name: string | null
  owner_email: string | null
  students_count: number
  teachers_count: number
  created_at: string
}

interface Props {
  schools: SchoolRow[]
}

export default function Index({ schools }: Props) {
  return (
    <PlatformLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Escolas</h1>
            <p className="text-sm text-muted-foreground">{schools.length} cadastrada{schools.length === 1 ? '' : 's'}</p>
          </div>
          <Link href="/platform/schools/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nova escola
            </Button>
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Todas as escolas</CardTitle>
          </CardHeader>
          <CardContent>
            {schools.length === 0 ? (
              <div className="py-12 text-center space-y-3">
                <p className="text-muted-foreground">Nenhuma escola cadastrada ainda.</p>
                <Link href="/platform/schools/new">
                  <Button>Cadastrar primeira escola</Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="py-2 font-medium">Escola</th>
                      <th className="py-2 font-medium">Status</th>
                      <th className="py-2 font-medium">Owner</th>
                      <th className="py-2 font-medium">Cidade</th>
                      <th className="py-2 font-medium text-right">Alunos</th>
                      <th className="py-2 font-medium text-right">Profs</th>
                      <th className="py-2 font-medium">Cadastro</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.map((s) => (
                      <tr key={s.id} className={`border-b last:border-0 hover:bg-muted/30 ${s.active ? '' : 'opacity-60'}`}>
                        <td className="py-3">
                          <p className="font-medium">{s.name}</p>
                          {s.slug && <p className="text-xs text-muted-foreground">/{s.slug}</p>}
                        </td>
                        <td className="py-3">
                          {s.active ? (
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300">Ativa</Badge>
                          ) : (
                            <Badge variant="destructive">Desativada</Badge>
                          )}
                        </td>
                        <td className="py-3">
                          {s.owner_name ? (
                            <>
                              <p>{s.owner_name}</p>
                              <p className="text-xs text-muted-foreground">{s.owner_email}</p>
                            </>
                          ) : (
                            <span className="text-muted-foreground italic">sem owner</span>
                          )}
                        </td>
                        <td className="py-3 text-muted-foreground">
                          {[s.city, s.state].filter(Boolean).join(' / ') || '-'}
                        </td>
                        <td className="py-3 text-right">{s.students_count}</td>
                        <td className="py-3 text-right">{s.teachers_count}</td>
                        <td className="py-3 text-muted-foreground">{s.created_at}</td>
                        <td className="py-3 text-right">
                          <Link href={`/platform/schools/${s.id}`} className="text-primary hover:underline">
                            ver
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PlatformLayout>
  )
}
